// frontend/src/admin/MusicAdminPage.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import adminApi from '../../shared/api/AxiosAdmin';
import './MusicAdminPage.css';

type MusicTrack = {
  _id: string;
  title: string;
  artist?: string;
  duration?: number;
  url?: string;
  s3Url?: string;
  audioUrl?: string;
  audioKey?: string;
  coverUrl?: string;
  coverKey?: string;
};

function getTrackUrl(t: MusicTrack): string {
  return t.audioUrl || t.url || t.s3Url || '';
}

export default function MusicAdminPage() {
  const navigate = useNavigate();

  // Admin gate states
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [adminOk, setAdminOk] = useState(false);

  // Page states
  const [tracks, setTracks] = useState<MusicTrack[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // per-row cover upload state
  const [coverBusy, setCoverBusy] = useState<Record<string, boolean>>({});
  const rowCoverInputs = useRef<Record<string, HTMLInputElement | null>>({});
  const fileRef = useRef<HTMLInputElement | null>(null);

  // ── HARD GATE: verify token is admin before doing anything ────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get('/admin/auth/whoami');
        if (!cancelled && data?.ok && data?.admin) {
          setAdminOk(true);
        } else if (!cancelled) {
          navigate('/admin/login', { replace: true, state: { next: '/upload-music' } });
        }
      } catch {
        if (!cancelled) {
          navigate('/admin/login', { replace: true, state: { next: '/upload-music' } });
        }
      } finally {
        if (!cancelled) setCheckingAdmin(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // Load tracks (public GET, but only after we confirmed admin to avoid showing UI to non-admins)
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminApi.get('/music');
      const items = Array.isArray(res.data) ? res.data : res.data?.tracks ?? [];
      setTracks(items);
    } catch (e) {
      console.error('Failed to fetch tracks', e);
      alert('Failed to fetch tracks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adminOk) load();
  }, [adminOk, load]);

  // Logout clears token and returns to admin login
  const logout = useCallback(async () => {
    try {
      await adminApi.post('/admin/auth/logout');
    } catch {}
    finally {
      navigate('/admin/login', { replace: true });
    }
  }, [navigate]);

  // Upload new track (admin-protected endpoint)
  const onUploadNewTrack = async () => {
    if (!file) return alert('Select an audio file');
    if (!file.type?.startsWith('audio/')) return alert('Only audio files are allowed');
    if (!title.trim() || !artist.trim()) return alert('Enter title and artist');

    const form = new FormData();
    form.append('music', file);
    form.append('title', title.trim());
    form.append('artist', artist.trim());

    try {
      setBusy(true);
      await adminApi.post('/music', form);
      setFile(null);
      setTitle('');
      setArtist('');
      if (fileRef.current) fileRef.current.value = '';
      await load();
      alert('✅ Upload successful');
    } catch (e: any) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.msg || e?.response?.data?.error || e?.message || '❌ Upload failed';
      alert(msg);
      if (status === 401 || status === 403) {
        navigate('/admin/login', { replace: true, state: { next: '/upload-music' } });
      }     
    } finally {
      setBusy(false);
    }
  };

  // Inline cover upload
  const openCoverPicker = (id: string) => rowCoverInputs.current[id]?.click();

  const onPickRowCover = async (track: MusicTrack, file: File | null) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) return alert('Cover must be an image');

    setCoverBusy(prev => ({ ...prev, [track._id]: true }));
    const form = new FormData();
    form.append('cover', file);

    try {
      const { data } = await adminApi.patch(`/music/${track._id}/cover`, form);
       if (data?.track) {
        setTracks(prev => prev.map(t => (t._id === track._id ? { ...t, ...data.track } : t)));
      } else {
        await load();
      }
    } catch (e: any) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.msg || e?.response?.data?.error || e?.message || '❌ Cover upload failed';
      alert(msg);
      if (status === 401 || status === 403) {
        navigate('/admin/login', { replace: true, state: { next: '/upload-music' } });
      }      
    } finally {
      setCoverBusy(prev => ({ ...prev, [track._id]: false }));
      const input = rowCoverInputs.current[track._id];
      if (input) input.value = '';
    }
  };

  // ── Render gates ──────────────────────────────────────────────────────────
  if (checkingAdmin) {
    return (
      <div id="music-admin" className="min-h-screen w-full bg-black p-8">
        <h1 className="page-title">🎵 Music Admin</h1>
        <p className="muted">Checking access…</p>
      </div>
    );
  }
  if (!adminOk) {
    // We already redirected, this is just a safety blank
    return null;
  }

  // ── UI ────────────────────────────────────────────────────────────────────
  return (
    <div id="music-admin" className="min-h-screen w-full bg-black p-8">


      <div className="header-row">
        <h1 className="page-title">🎵 Music Admin</h1>
      </div>

      {/* Upload new track (audio) */}
      <div className="panel mb-10 p-6 border rounded-lg space-y-4 shadow-sm">
        <h2 className="section-title">⬆️ Upload New Track</h2>
        <div className="new-row">
          <div className="new-cell">
            <label className="label">Audio File</label>
            <input
              ref={fileRef}
              type="file"
              accept="audio/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              disabled={busy}
              className="file-input"
            />
          </div>
          <div className="new-cell grow">
            <input
              className="text-input"
              type="text"
              placeholder="Track Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="new-cell grow">
            <input
              className="text-input"
              type="text"
              placeholder="Artist"
              value={artist}
              onChange={(e) => setArtist(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="new-cell">
            <button
              onClick={onUploadNewTrack}
              disabled={busy}
              className="btn-primary px-4 py-2 rounded w-full"
            >
              {busy ? 'Uploading…' : 'Upload Track'}
            </button>
          </div>
        </div>
      </div>

      {/* Track list */}
      {loading ? (
        <div className="muted">Loading tracks…</div>
      ) : (
        <ul className="track-list">
          {tracks.map((t) => (
            <li key={t._id} className="track-item panel border rounded-lg shadow-sm">
              <div className="track-row">
                {/* LEFT: cover square (click to upload/change) */}
                <div className="cover-col">
                  <div
                    className={`cover-box ${coverBusy[t._id] ? 'busy' : ''}`}
                    onClick={() => !coverBusy[t._id] && openCoverPicker(t._id)}
                    role="button"
                    title={t.coverUrl ? 'Change cover' : 'Upload cover'}
                  >
                    {t.coverUrl ? (
                      <img
                        src={t.coverUrl}
                        alt={`${t.title} cover`}
                        loading="lazy"
                        className="cover-img"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      <div className="cover-placeholder">
                        <span className="upload-icon">⬆︎</span>
                        <span className="upload-text">Add Cover</span>
                      </div>
                    )}
                    {coverBusy[t._id] && <div className="cover-overlay">Uploading…</div>}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    ref={(el) => { rowCoverInputs.current[t._id] = el; }}
                    style={{ display: 'none' }}
                    onChange={(e) => onPickRowCover(t, e.target.files?.[0] || null)}
                  />
                </div>

                {/* RIGHT: Title, meta, actions, player */}
                <div className="meta-col">
                  <div className="meta-top">
                    <div className="title-wrap">
                      <h2 className="track-title">{t.title}</h2>
                      <p className="track-sub muted">
                        By {t.artist || 'Unknown'}{t.duration ? ` • ${t.duration}s` : ''}
                      </p>
                    </div>
                    <button
                      onClick={async () => {
                        if (!window.confirm('Delete this track?')) return;
                        try {
                          await adminApi.delete(`/music/${t._id}`);
                          setTracks(prev => prev.filter(x => x._id !== t._id));
                        } catch (e: any) {
                          const status = e?.response?.status;
                          const msg = e?.response?.data?.msg || e?.response?.data?.error || e?.message || '❌ Delete failed';
                          alert(msg);
                          if (status === 401 || status === 403) {
                            navigate('/admin/login', { replace: true, state: { next: '/upload-music' } });
                          }                          
                        }
                      }}
                      className="btn-danger"
                    >
                      🗑️ Delete
                    </button>

                  
                  </div>

                  {getTrackUrl(t) ? (
                    <audio controls src={getTrackUrl(t)} className="player" />
                  ) : (
                    <div className="muted small">No audio URL on this record.</div>
                  )}
                </div>

             
              </div>
            
            </li>
          ))}
          {tracks.length === 0 && <li className="muted">No tracks yet.</li>}
        </ul>
      )}
    </div>
  );
}
